import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useState, useMemo } from "react";
import useStore from "../../../helpers/useStore";
import { UserContext } from "../../../stores/User/User.provider";
import { Add, MoreVert, Search, ViewColumn } from "@mui/icons-material";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useMasterTables } from "../../../stores/MasterTables";
import { useUsersByLocation } from "../../../stores/User/user.queries";
import { userKeys } from "../../../stores/User/user.keys";
import notificationStore from "../../../stores/Notification/notificationStore";
import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import FlatCard from "../../../atoms/FlatCard";
import type { UserDto } from "../../../api-client";
import CalendarColumnOrderDialog from "./CalendarColumnOrderDialog";

// Rows in this table always come from the backend and therefore have an id
// (UserDto declares it optional only because it doubles as a create payload).
type UserRow = UserDto & { id: string };

type SortDirection = "ASC" | "DESC";

/** Sorting is client-side: the whole list is already in memory (no paging). */
const DEFAULT_SORT: Record<string, SortDirection> = { firstName: "ASC" };

// German collation: "Ö" sorts next to "O" rather than after "Z", and embedded
// numbers compare numerically.
const collator = new Intl.Collator("de", { sensitivity: "base", numeric: true });

/**
 * Compare two cell values. Blanks always sort last, in both directions — a
 * column of "-" at the top is never what the admin was asking for.
 */
const compareCells = (
  a: string | number | null,
  b: string | number | null,
  direction: SortDirection
) => {
  const aEmpty = a === null || a === "";
  const bEmpty = b === null || b === "";
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

  const result =
    typeof a === "number" && typeof b === "number" ? a - b : collator.compare(String(a), String(b));
  return direction === "DESC" ? -result : result;
};

interface UsersProps {
  /** Open a record in the detail view; `{}` opens an empty draft for creation. */
  onSelectUser: (user: UserDto | null) => void;
  selectedLocation: string;
  setSelectedLocation: (locationId: string) => void;
}

const Users = observer(({ onSelectUser, selectedLocation, setSelectedLocation }: UsersProps) => {
  const UserStore = useStore(UserContext);
  const { activateOrDeactivateUser, hardDeleteUser } = UserStore;
  // Cached per location, so closing the detail view repaints the list from
  // cache instead of re-fetching every user of the location on each return.
  const { data: userData = [], isLoading: loading } = useUsersByLocation(selectedLocation);
  const queryClient = useQueryClient();
  const [menu, setMenu] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  // The archive is a separate view, not an addition to the active list: on it
  // shows only deactivated users, off shows only active ones.
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [searched, setSearched] = useState<string>("");
  const [sortBy, setSortBy] = useState<Record<string, SortDirection>>(DEFAULT_SORT);
  const [selectedIndex, setSelectedIndexState] = useState<number | null>(null);
  const [columnOrderOpen, setColumnOrderOpen] = useState<boolean>(false);
  // Adapter for GenericInfiniteTable's setter signature (updater receives a
  // plain number; -1 stands in for "no selection yet").
  const setSelectedIndex = (index: number | ((prevIndex: number) => number)) =>
    setSelectedIndexState((prev) => (typeof index === "function" ? index(prev ?? -1) : index));
  const { data: masterTablesData } = useMasterTables();

  const locationsOptions = (masterTablesData || [])
    .filter((obj: any) => "location" in obj)
    .map((obj: any) => obj.location);

  // Map of location id -> display name, used to render the "Standort" column
  // as a readable name instead of a UUID.
  const locationNameById = useMemo(
    () =>
      new Map<string, string>(
        ((locationsOptions[0] as any[]) || []).map((loc: any) => [
          loc.id?.toString(),
          (loc.name ?? "").toString(),
        ])
      ),
    [locationsOptions]
  );

  const getLocationName = (row: any): string => {
    const primaryLocationId = row.therapistLocation?.id ?? row.therapistLocationId ?? "";
    return primaryLocationId
      ? locationNameById.get(primaryLocationId.toString()) || primaryLocationId
      : "";
  };

  const handleMenuClose = () => {
    setMenu(null);
  };

  const handleEditClick = (selectedUser: any) => {
    if (selectedUser) {
      onSelectUser(selectedUser);
      handleMenuClose();
    }
  };

  const handleActivateDeactivateClick = async (selectedUser: any) => {
    try {
      if (selectedUser.deletedAt) {
        await activateOrDeactivateUser(selectedUser.id, false);
        notificationStore.showMessage("Benutzer wurde reaktiviert", "success");
      } else {
        await activateOrDeactivateUser(selectedUser.id, true);
        notificationStore.showMessage("Benutzer wurde deaktiviert", "success");
      }

      fetchUsers();
    } catch (error: any) {
      console.error("Error during delete:", error);
      const msg = error?.response?.data?.message || "Aktion fehlgeschlagen";
      notificationStore.showMessage(msg, "error", error);
    } finally {
      handleMenuClose();
    }
  };

  // State für Hard-Delete-Preflight-Dialog
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightData, setPreflightData] = useState<any>(null);
  const [preflightUser, setPreflightUser] = useState<any>(null);

  const handleHardDeleteClick = async (selectedUser: any) => {
    try {
      setPreflightLoading(true);
      const preflight = await UserStore.hardDeletePreflight(selectedUser.id);
      setPreflightUser(selectedUser);
      setPreflightData(preflight);
      setPreflightOpen(true);
    } catch (error: any) {
      // Fehler sind bereits im Store benachrichtigt
    } finally {
      setPreflightLoading(false);
      handleMenuClose();
    }
  };

  const handleConfirmHardDelete = async () => {
    if (!preflightUser) return;
    try {
      await hardDeleteUser(preflightUser.id);
      notificationStore.showMessage("Benutzer wurde endgültig gelöscht", "success");
      fetchUsers();
    } catch (error: any) {
      const msg =
        error?.message || error?.response?.data?.message || "Endgültiges Löschen nicht möglich.";
      notificationStore.showMessage(msg, "error", error);
    } finally {
      setPreflightOpen(false);
      setPreflightData(null);
      setPreflightUser(null);
    }
  };

  const handleArchiveFromDialog = async () => {
    if (!preflightUser) return;
    try {
      await activateOrDeactivateUser(preflightUser.id, true);
      notificationStore.showMessage("Benutzer wurde deaktiviert", "success");
      fetchUsers();
    } catch (error: any) {
      const msg = error?.response?.data?.message || "Aktion fehlgeschlagen";
      notificationStore.showMessage(msg, "error", error);
    } finally {
      setPreflightOpen(false);
      setPreflightData(null);
      setPreflightUser(null);
    }
  };

  const handleMoreButtonClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedUser(row);
    setMenu(event.currentTarget);
    event.stopPropagation();
  };

  // Whole scope, not just this location: a (de)activated user is in every copy.
  const fetchUsers = () => {
    void queryClient.invalidateQueries({ queryKey: userKeys.usersByLocation.all() });
  };

  const handleLocationChange = (selectedLocationId: string) => {
    setSelectedLocation(selectedLocationId);
  };

  // One accessor per sortable column, returning the value the column *displays*
  // — so "Rollen" and "Standort" sort by the readable text the admin sees, not
  // by the underlying ids.
  const sortAccessors = useMemo<Record<string, (row: any) => string | number | null>>(
    () => ({
      firstName: (row) => row.firstName || "",
      lastName: (row) => row.lastName || "",
      username: (row) => row.username || "",
      email: (row) => row.email || "",
      roles: (row) => (row.roles || []).map((role: any) => role.name || role.id).join(", "),
      location: (row) => getLocationName(row),
      createdAt: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : null),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationNameById]
  );

  // Non-destructive filtering and sorting: the raw fetch result stays in
  // `userData`, so clearing the search box, toggling the archive switch or
  // re-sorting never requires a refetch.
  const visibleRows = useMemo(() => {
    let rows = userData.filter((row: any) => (showArchived ? !!row.deletedAt : !row.deletedAt));
    const searchKey = searched.trim().toLowerCase();
    if (searchKey) {
      rows = rows.filter((row: any) => {
        return (
          row.lastName?.toLowerCase().includes(searchKey) ||
          row.firstName?.toLowerCase().includes(searchKey) ||
          row.email?.toLowerCase().includes(searchKey) ||
          getLocationName(row).toLowerCase().includes(searchKey)
        );
      });
    }

    const [sortKey, sortDirection] = Object.entries(sortBy)[0] || [];
    const accessor = sortKey ? sortAccessors[sortKey] : undefined;
    if (accessor && sortDirection) {
      rows = [...rows].sort((a, b) => {
        const primary = compareCells(accessor(a), accessor(b), sortDirection);
        if (primary !== 0) return primary;
        // Stable, predictable tie-break so equal cells don't shuffle between
        // renders (e.g. every row in a single-location list).
        return (
          compareCells(a.lastName || "", b.lastName || "", "ASC") ||
          compareCells(a.firstName || "", b.firstName || "", "ASC")
        );
      });
    }

    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, showArchived, searched, locationNameById, sortBy, sortAccessors]);

  // Cycle asc -> desc -> back to the default (first name, ascending). The row
  // at a given index changes under a re-sort, so the highlight is dropped.
  const handleSortChange = (columnKey: string, direction?: SortDirection) => {
    setSortBy(direction ? { [columnKey]: direction } : DEFAULT_SORT);
    setSelectedIndexState(null);
  };

  const columns: ColumnDef<UserRow>[] = useMemo(
    () => [
      { key: "firstName", header: "Vorname", sortable: true, render: (u) => u.firstName || "-" },
      { key: "lastName", header: "Nachname", sortable: true, render: (u) => u.lastName || "-" },
      {
        key: "username",
        header: "Benutzername",
        sortable: true,
        render: (u) => u.username || "-",
      },
      { key: "email", header: "Email", sortable: true, render: (u) => u.email || "-" },
      {
        key: "roles",
        header: "Rollen",
        sortable: true,
        render: (u) => (u.roles || []).map((role: any) => role.name || role.id).join(", ") || "-",
      },
      // Single "Standort" column: the user's primary work location, resolved
      // to its display name instead of a UUID.
      {
        key: "location",
        header: "Standort",
        sortable: true,
        render: (u) => getLocationName(u) || "-",
      },
      {
        key: "createdAt",
        header: "Erstellt am",
        sortable: true,
        render: (u: any) => (u.createdAt ? format(new Date(u.createdAt), "dd.MM.yyyy") : "-"),
      },
      {
        key: "actions",
        header: "Aktionen",
        width: "60px",
        render: (u) => (
          <IconButton size="small" onClick={(event) => handleMoreButtonClick(event, u)}>
            <MoreVert />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationNameById]
  );

  // Toolbar modelled after the /registry tabs: compact controls inside a
  // FlatCard instead of the previous full-height header box.
  const toolbar = (
    <FlatCard
      title={showArchived ? "Archivierte Benutzer" : "Benutzer"}
      sx={{ mb: 1 }}
      sxContent={{ p: 1.5 }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Autocomplete
          size="small"
          sx={{ minWidth: 200 }}
          options={
            locationsOptions[0]?.map((option: any) => ({
              label: option.name?.toString(),
              value: option.id?.toString(),
            })) || []
          }
          getOptionLabel={(option: any) => option.label || ""}
          isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
          value={
            locationsOptions[0]
              ?.map((option: any) => ({
                label: option.name?.toString(),
                value: option.id?.toString(),
              }))
              .find((option: any) => option.value === selectedLocation) || null
          }
          onChange={(_, newValue: any) => {
            handleLocationChange(newValue ? newValue.value : "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Standort" InputLabelProps={{ shrink: true }} />
          )}
        />

        <TextField
          size="small"
          placeholder="Suchen"
          value={searched}
          onChange={(e) => setSearched(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                // The two lists have nothing in common, so a highlight carried
                // over from the other one would point at an unrelated row.
                setSelectedIndexState(null);
              }}
            />
          }
          label="Archivierte Benutzer"
        />

        {/* "contained" (the theme's default variant) rather than "outlined":
            palette.primary.main is #fff, so an outlined button renders white
            text on this white toolbar. The app's other outlined buttons only
            appear in DialogActions, which the theme paints topLevelBackground. */}
        <Button
          variant="contained"
          size="small"
          startIcon={<ViewColumn />}
          onClick={() => setColumnOrderOpen(true)}
          disabled={!selectedLocation}
          sx={{ ml: "auto" }}
        >
          Spaltenreihenfolge
        </Button>

        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => onSelectUser({})}>
          Neu
        </Button>
      </Box>
    </FlatCard>
  );

  // Refetch on close so a saved order is reflected in the table's rows.
  // release/0.4 replaced the 0.3 line's `fetchData(locationId)` with a
  // query-cache invalidation that takes no argument and covers every location.
  const handleColumnOrderClose = () => {
    setColumnOrderOpen(false);
    fetchUsers();
  };

  return (
    <>
      {columnOrderOpen && (
        <CalendarColumnOrderDialog
          open={columnOrderOpen}
          onClose={handleColumnOrderClose}
          locationId={selectedLocation}
          locationName={locationNameById.get(selectedLocation) || selectedLocation}
          // Deliberately the unfiltered fetch result, not `visibleRows`: saving
          // replaces the location's whole column order, so search/archive
          // filtering must not silently drop users out of it.
          users={userData}
        />
      )}

      {/* Preflight-Dialog für Hard-Delete */}
      <Dialog open={preflightOpen} onClose={() => setPreflightOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Endgültiges Löschen prüfen</DialogTitle>
        <DialogContent dividers>
          {preflightLoading ? (
            <Typography>Prüfung läuft…</Typography>
          ) : (
            <>
              <Typography gutterBottom>
                Benutzer: {preflightUser?.firstName} {preflightUser?.lastName}
              </Typography>
              {preflightData?.canDelete ? (
                <Typography color="text.primary" sx={{ fontWeight: "bold" }}>
                  Warnung: Diese Aktion löscht den Benutzer dauerhaft und kann nicht rückgängig
                  gemacht werden.
                </Typography>
              ) : (
                <>
                  <Typography color="error" gutterBottom>
                    Endgültiges Löschen aktuell nicht möglich. Gründe:
                  </Typography>
                  <List dense>
                    {(preflightData?.reasons || []).map((r: string, idx: number) => (
                      <ListItem key={idx} sx={{ py: 0 }}>
                        <Typography variant="body2">• {r}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="body2" color="text.secondary">
                    Bitte statt Löschung „Deaktivieren/Archivieren“ verwenden.
                  </Typography>
                </>
              )}

              {preflightData?.details && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Details: Termine: {preflightData.details.appointmentCount} · Zeit-Slots:{" "}
                    {preflightData.details.timeSlotCount} · Arbeitspläne:{" "}
                    {preflightData.details.workPlanCount}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreflightOpen(false)}>Abbrechen</Button>
          <Button onClick={handleArchiveFromDialog} variant="outlined" color="primary">
            Deaktivieren/Archivieren
          </Button>
          <Button
            onClick={handleConfirmHardDelete}
            variant="contained"
            color="error"
            disabled={!preflightData?.canDelete}
          >
            Endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Height-bounded flex column (registry pattern) so the table is its own
          scroll container and the sticky header actually sticks. */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 220px)",
          minHeight: 0,
        }}
      >
        <GenericInfiniteTable<UserRow>
          data={visibleRows as UserRow[]}
          columns={columns}
          hasMoreData={false}
          fetchMoreData={() => undefined}
          isLoading={loading}
          onRowClick={(row) => onSelectUser(row)}
          scrollId="adminUsersScroll"
          toolbar={toolbar}
          sortBy={sortBy}
          onSortChange={handleSortChange as (columnKey: string, direction?: "ASC" | "DESC") => void}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          endMessageLabel=""
          emptyMessage={
            showArchived ? "Keine archivierten Benutzer gefunden" : "Keine Benutzer gefunden"
          }
        />
      </Box>

      <Menu id="actions-menu" anchorEl={menu} open={Boolean(menu)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEditClick(selectedUser)}>Bearbeiten</MenuItem>
        {!selectedUser?.deletedAt && (
          <MenuItem onClick={() => handleActivateDeactivateClick(selectedUser)}>
            Deaktivieren
          </MenuItem>
        )}
        {selectedUser?.deletedAt && (
          <MenuItem onClick={() => handleActivateDeactivateClick(selectedUser)}>
            Aktivieren
          </MenuItem>
        )}
        <MenuItem onClick={() => handleHardDeleteClick(selectedUser)} sx={{ color: "red" }}>
          Dauerhaft löschen
        </MenuItem>
      </Menu>
    </>
  );
});
export default Users;
